<template>
  <section test-id="main">
    <el-form
      label-position="top"
      :model="depositForm"
      ref="depositForm"
      :rules="rules"
      label-width="80px"
      size="normal"
    >
      <div class="info-icon">
        <el-popover
          placement="top-start"
          width="350"
          trigger="hover"
          :content="depositMethodTooltipContent"
          popper-class="bg-gray-900 text-white break-normal text-center"
        >
          <el-button slot="reference"><info-icon /></el-button>
        </el-popover>
      </div>
      <el-form-item class="w-full" label="Deposit Method">
        <el-select
          v-model="depositForm.option"
          placeholder="Select"
          @change="selectDepositOption"
          class="w-full"
        >
          <el-option class="h-14" value="new deposit" label="New Deposit">
            <div class="flex items-center gap-x-4 h-14">
              <new-deposit-icon />
              <div>
                <p class="text-sm font-bold text-root-2 capitalize">
                  New Deposit
                </p>
                <p class="text-xs md:flex hidden">
                  Local Transfer, Wire Transfer & Direct Debit
                </p>
              </div>
              <div class="absolute right-5">
                <small-check v-if="isNewDepositMethod" />
              </div>
            </div>
          </el-option>
          <el-option
            v-for="(item, index) in filteredDepositAccounts"
            :key="index"
            :value="item.currency"
            :label="`${item.currency} Bal:  ${parseMoneyAll(
              item.currentBalance,
              item.currency
            )}`"
            :disabled="!accountIsViable(item)"
            class="h-16 flex items-center"
            :class="[
              {},
              {
                'cursor-not-allowed opacity-20': !accountIsViable(item)
              }
            ]"
          >
            <el-tooltip
              class="item"
              effect="dark"
              :content="getAccountToolTipContent(item)"
              :disabled="accountIsViable(item)"
              placement="right"
            >
              <div class="flex justify-between items-center">
                <div class="flex gap-x-4">
                  <currency-icons
                    :currency="getCurrencyCountry(item.currency)"
                    size="w-8 h-8"
                  />
                  <div>
                    <p class="text-sm font-bold text-root-2 capitalize">
                      {{ item.currency }} Holding Balance
                    </p>
                    <p class="text-xs text-fg-muted font-normal">
                      Bal:
                      {{ parseMoneyAll(item.currentBalance, item.currency) }}
                    </p>
                  </div>
                </div>
                <div class="absolute right-5">
                  <small-check v-if="depositForm.option === item.currency" />
                </div>
              </div>
            </el-tooltip>
          </el-option>
        </el-select>
      </el-form-item>
    </el-form>
  </section>
</template>
<script>
import { GetCurrencyCountry, ParseMoneyAll } from '@/utils/helpers.js'
import totalAccounts from '@/mixins/getBalance.js'
import hasWalletAddress from '@/mixins/deposit.js'
import tenant from '@/mixins/tenant'
import terminal from '@/mixins/terminal.js'
import country from '@/mixins/getCountryDetails.js'
import { ASSETS } from '@/utils/constants.js'
export default {
  mixins: [totalAccounts, hasWalletAddress, tenant, terminal, country],
  props: {
    use: {
      type: String
    }
  },
  data () {
    return {
      isLoading: false,
      loading: false,
      depositMethodTooltipContent:
        'This would be the method you want to deposit in, the deposit methods are Local transfer, Wire transfer, Direct debit and your Holdings account.',
      depositForm: {
        option: ''
      },
      rules: {
        option: [
          {
            required: true,
            message: 'Please select',
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  components: {
    SmallCheck: () => import('@/assets/v2/icons/SmallCheck.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    NewDepositIcon: () => import('@/assets/v2/icons/NewDepositIcon.vue'),
    InfoIcon: () => import('@/assets/v2/icons/InfoIcon.vue')
  },
  computed: {
    isEditUse () {
      return this.use === 'edit'
    },
    selectedDepositOption () {
      return this.$store.state.terminal.depositMethod
    },
    currencyTransferDetails () {
      return this.$store.state.auth.transferDetails
    }
  },
  mounted () {
    this.prefillDepositMethod()
  },
  methods: {
    prefillDepositMethod () {
      // Check if there's an account in filteredDepositAccounts matching the country.currency
      const account = this.filteredDepositAccounts.find(
        (item) => item.currency === this.country.currency
      )
      const isViable = account && this.accountIsViable(account)
      this.depositForm.option = isViable ? account.currency : 'new deposit'
      this.$store.dispatch('terminal/setDepositMethod', this.depositForm.option)
    },
    accountIsViable (account) {
      // valid transfer checks if the currency pair is enabled for transfers
      const validTransfer = this.currencyTransferDetails.some(
        (item) =>
          item.from === account.currency &&
          item.to === this.country2.currency &&
          item.is_enabled
      )
      return account.currentBalance > 0 && validTransfer
    },
    getAccountToolTipContent (account) {
      const accountIsNotFunded = account.currentBalance <= 0
      // valid transfer checks if the currency pair is enabled for transfers
      const validTransfer = this.currencyTransferDetails.some(
        (item) =>
          item.from === account.currency &&
          item.to === this.country2.currency &&
          item.is_enabled
      )
      if (accountIsNotFunded) return 'Insufficient funds'
      if (!validTransfer) {
        return `Can't transfer from ${account.currency} to ${this.country2.currency}`
      }
    },

    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    handleActiveCurrencyChange () {
      // when the active currency changes (i.e on the select currency dialog, this will check if there is a deposit account for that currency and if there isn't the deposit method will be new deposit)
      let accountExists = {}
      accountExists = this.filteredDepositAccounts.find(
        (item) => item.currency === this.country.currency
      )
      if (accountExists?.currentBalance > 0) {
        this.$store.dispatch(
          'terminal/setDepositMethod',
          accountExists.currency
        )
        this.depositForm.option = accountExists.currency
      } else {
        this.$store.dispatch('terminal/setDepositMethod', 'new deposit')
        this.depositForm.option = 'new deposit'
      }
    },
    setActiveCountry () {
      const country = this.getCurrencyCountry(this.depositForm.option)
      this.$store.dispatch('common/setActiveCountry', country)
    },
    selectDepositOption () {
      this.$store.dispatch('terminal/setDepositMethod', this.depositForm.option)
      if (!this.isNewDepositMethod) {
        this.setActiveCountry()
        this.getNextAvailableCurrencyOption(this.depositForm.option)
      }
    }
  },
  watch: {
    'country.currency': function () {
      this.handleActiveCurrencyChange()
    }
  }
}
</script>
<style scoped>
.info-icon {
  margin-left: 105px;
  margin-bottom: -30px;
}
>>> .el-button {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
}
.classes {
  background-color: blue;
}
>>> .el-popover {
  background: blue !important;
}
>>> .el-input--prefix .el-input__inner {
  padding-left: 70% !important;
}
>>> .el-input__inner {
  border-radius: 8px;
}
</style>
